import React, { useState, useEffect } from 'react';
import {
  X,
  History,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Layers,
  FileText,
  Search,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ShieldAlert,
} from 'lucide-react';
import { getNewsAgentHistory, getNewsAgentRunDetail } from '../../../services/newsNewsletterApi';

export default function AgentRunHistoryModal({ isOpen, onClose }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });
  const [selectedRun, setSelectedRun] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadHistory(1);
    } else {
      setSelectedRun(null);
    }
  }, [isOpen]);

  const loadHistory = async (targetPage = 1) => {
    try {
      setLoading(true);
      const res = await getNewsAgentHistory({ page: targetPage, limit: 8 });
      if (res?.success && res.data) {
        setRuns(res.data.runs || []);
        setPagination(res.data.pagination || { page: targetPage, limit: 8, total: 0, totalPages: 1 });
      }
    } catch (err) {
      console.error('Erreur chargement historique:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInspectRun = async (runId) => {
    try {
      setDetailLoading(true);
      const res = await getNewsAgentRunDetail(runId);
      if (res?.success && res.data) {
        setSelectedRun(res.data);
      }
    } catch (err) {
      console.error('Erreur chargement détail run:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="an-agent-modal-backdrop" onClick={onClose}>
      <div className="an-agent-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="an-agent-modal-header">
          <div className="an-agent-modal-title-wrap">
            <div className="an-agent-modal-icon">
              <History size={20} />
            </div>
            <div>
              <h3 className="an-agent-modal-title">
                {selectedRun ? `Détails de la collecte : ${selectedRun.runId}` : "Historique des collectes de l'Agent IA"}
              </h3>
              <p className="an-agent-modal-subtitle">
                {selectedRun
                  ? `Exécutée le ${new Date(selectedRun.startedAt).toLocaleString('fr-FR')}`
                  : 'Traçabilité et audit des cycles de veille automatique et manuelle'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="an-agent-modal-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="an-agent-modal-body">
          {selectedRun ? (
            /* Run Detail View */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div className="an-agent-modal-subnav">
                <button
                  onClick={() => setSelectedRun(null)}
                  className="an-agent-modal-btn-back"
                >
                  <ChevronLeft size={16} /> Retour à la liste
                </button>
                <div className="an-agent-modal-status-group">
                  <span
                    className={`an-agent-badge-pill ${
                      selectedRun.status === 'COMPLETED'
                        ? 'completed'
                        : selectedRun.status === 'RUNNING'
                        ? 'running'
                        : 'failed'
                    }`}
                  >
                    {selectedRun.status}
                  </span>
                  <span className="an-agent-badge-pill mode">
                    {selectedRun.mode}
                  </span>
                  <span className="an-agent-badge-pill" style={{ background: '#F1F5F9', color: '#475569' }}>
                    {selectedRun.triggeredBy} {selectedRun.triggeredByEmail ? `(${selectedRun.triggeredByEmail})` : ''}
                  </span>
                </div>
              </div>

              {/* Stats KPI grid */}
              <div className="an-agent-modal-kpi-grid">
                <div className="an-agent-modal-kpi-item">
                  <span className="an-agent-modal-kpi-label">Durée</span>
                  <p className="an-agent-modal-kpi-val">{selectedRun.durationSeconds}s</p>
                </div>
                <div className="an-agent-modal-kpi-item">
                  <span className="an-agent-modal-kpi-label">Découverts</span>
                  <p className="an-agent-modal-kpi-val">{selectedRun.totalDiscovered}</p>
                </div>
                <div className="an-agent-modal-kpi-item emerald">
                  <span className="an-agent-modal-kpi-label" style={{ color: '#047857' }}>Rédigés (IA)</span>
                  <p className="an-agent-modal-kpi-val" style={{ color: '#065F46' }}>{selectedRun.totalDrafted}</p>
                </div>
                <div className="an-agent-modal-kpi-item blue">
                  <span className="an-agent-modal-kpi-label" style={{ color: '#1D4ED8' }}>Ingérés</span>
                  <p className="an-agent-modal-kpi-val" style={{ color: '#1E40AF' }}>{selectedRun.totalIngested}</p>
                </div>
                <div className="an-agent-modal-kpi-item rose">
                  <span className="an-agent-modal-kpi-label" style={{ color: '#BE123C' }}>AO Exclus</span>
                  <p className="an-agent-modal-kpi-val" style={{ color: '#9F1239' }}>{selectedRun.totalRejected}</p>
                </div>
                <div className="an-agent-modal-kpi-item amber">
                  <span className="an-agent-modal-kpi-label" style={{ color: '#B45309' }}>Doublons</span>
                  <p className="an-agent-modal-kpi-val" style={{ color: '#92400E' }}>{selectedRun.totalDuplicates}</p>
                </div>
              </div>

              {/* Error messages if any */}
              {selectedRun.errors && selectedRun.errors.length > 0 && (
                <div className="an-agent-error-alert">
                  <div className="an-agent-error-title">
                    <ShieldAlert size={16} /> Erreurs constatées pendant la collecte :
                  </div>
                  <ul className="an-agent-error-list">
                    {selectedRun.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Items List */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Publications traitées ({selectedRun.items?.length || 0})
                </h4>
                <div className="an-agent-modal-table-wrap">
                  <table className="an-agent-modal-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                        <th className="py-2.5 px-3">Source</th>
                        <th className="py-2.5 px-3">Titre</th>
                        <th className="py-2.5 px-3">Classification</th>
                        <th className="py-2.5 px-3">Statut</th>
                        <th className="py-2.5 px-3">Détails / Raison</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {selectedRun.items && selectedRun.items.length > 0 ? (
                        selectedRun.items.map((item, idx) => (
                          <tr key={idx} className="hover:bg-slate-50/70 transition">
                            <td className="py-2 px-3 font-semibold uppercase text-slate-700">
                              {item.source_id}
                            </td>
                            <td className="py-2 px-3 max-w-xs">
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-slate-800 hover:text-amber-600 font-medium line-clamp-1 flex items-center gap-1"
                              >
                                {item.title || item.url} <ExternalLink className="w-3 h-3 opacity-60 inline" />
                              </a>
                            </td>
                            <td className="py-2 px-3">
                              <span
                                className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                                  item.classification === 'NEWS'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : item.classification === 'TENDER'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {item.classification || 'N/A'}
                              </span>
                            </td>
                            <td className="py-2 px-3 font-semibold">
                              <span
                                className={`px-2 py-0.5 text-[10px] rounded ${
                                  item.status === 'INGESTED' || item.status === 'DRAFTED'
                                    ? 'bg-emerald-100 text-emerald-800'
                                    : item.status === 'DUPLICATE'
                                    ? 'bg-amber-100 text-amber-800'
                                    : item.status === 'REJECTED'
                                    ? 'bg-rose-100 text-rose-800'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {item.status}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-slate-500 max-w-sm text-[11px]">
                              {item.rejection_reason || item.proposal_code || '-'}
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="py-6 text-center text-slate-400">
                            Aucun élément individuel consigné pour cette exécution.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            /* Runs Table View */
            <div className="space-y-4">
              {loading ? (
                <div className="py-16 text-center text-slate-400 space-y-2">
                  <div className="w-8 h-8 mx-auto border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                  <p className="text-xs">Chargement de l'historique...</p>
                </div>
              ) : runs.length === 0 ? (
                <div className="py-16 text-center text-slate-400 space-y-2">
                  <History className="w-10 h-10 mx-auto opacity-30" />
                  <p className="text-sm font-medium">Aucune collecte enregistrée pour le moment.</p>
                  <p className="text-xs">Les exécutions automatiques et manuelles apparaîtront ici.</p>
                </div>
              ) : (
                <div className="an-agent-modal-table-wrap">
                  <table className="an-agent-modal-table">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-semibold">
                        <th className="py-3 px-4">Date & Heure</th>
                        <th className="py-3 px-3">Déclencheur</th>
                        <th className="py-3 px-3">Mode</th>
                        <th className="py-3 px-3">Statut</th>
                        <th className="py-3 px-3">Durée</th>
                        <th className="py-3 px-3 text-center">Découverts</th>
                        <th className="py-3 px-3 text-center">Rédigés</th>
                        <th className="py-3 px-3 text-center">Ingérés</th>
                        <th className="py-3 px-3 text-center">Rejetés</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {runs.map((r) => (
                        <tr key={r.runId} className="hover:bg-slate-50/80 transition">
                          <td className="py-3 px-4 font-medium text-slate-800">
                            {new Date(r.startedAt).toLocaleString('fr-FR', {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })}
                          </td>
                          <td className="py-3 px-3 text-slate-600 font-semibold">
                            <span
                              className={`px-2 py-0.5 rounded text-[10px] ${
                                r.triggeredBy === 'AUTOMATIC'
                                  ? 'bg-blue-50 text-blue-700'
                                  : 'bg-purple-50 text-purple-700'
                              }`}
                            >
                              {r.triggeredBy}
                            </span>
                          </td>
                          <td className="py-3 px-3 font-medium text-slate-700">{r.mode}</td>
                          <td className="py-3 px-3">
                            <span
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                                r.status === 'COMPLETED'
                                  ? 'bg-emerald-100 text-emerald-800'
                                  : r.status === 'RUNNING'
                                  ? 'bg-blue-100 text-blue-800 animate-pulse'
                                  : 'bg-rose-100 text-rose-800'
                              }`}
                            >
                              {r.status}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-slate-500">{r.durationSeconds}s</td>
                          <td className="py-3 px-3 text-center font-semibold text-slate-700">{r.totalDiscovered}</td>
                          <td className="py-3 px-3 text-center font-bold text-emerald-700">{r.totalDrafted}</td>
                          <td className="py-3 px-3 text-center font-bold text-blue-700">{r.totalIngested}</td>
                          <td className="py-3 px-3 text-center text-rose-600">{r.totalRejected}</td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              onClick={() => handleInspectRun(r.runId)}
                              disabled={detailLoading}
                              className="an-agent-btn-inspect"
                            >
                              Inspecter
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination controls */}
              {pagination.totalPages > 1 && (
                <div className="an-agent-pagination-bar">
                  <span>
                    Page {pagination.page} sur {pagination.totalPages} ({pagination.total} exécutions au total)
                  </span>
                  <div className="an-agent-pagination-btns">
                    <button
                      onClick={() => loadHistory(pagination.page - 1)}
                      disabled={pagination.page <= 1 || loading}
                      className="an-agent-pagination-btn"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => loadHistory(pagination.page + 1)}
                      disabled={pagination.page >= pagination.totalPages || loading}
                      className="an-agent-pagination-btn"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
